import { type ReactNode } from "react";
import { cn } from "../../utils/cn";

export interface NavItem {
  id: string;
  label: string;
  icon?: ReactNode;
  group?: string;   // undefined = ganz oben, ohne Gruppenkopf
  badge?: number;
}

interface WorkspaceNavProps {
  items: NavItem[];
  active: string;
  onChange: (id: string) => void;
}

/**
 * Sektions-Navigation im Server-Workspace.
 * Desktop (lg+): vertikale, nach Kategorien gruppierte Liste — uebersichtlich
 * auch bei vielen Bereichen. Mobil: kompakte horizontale Scroll-Leiste.
 */
export const WorkspaceNav = ({ items, active, onChange }: WorkspaceNavProps) => {
  // In Reihenfolge gruppieren (undefined-Gruppe zuerst, ohne Kopf).
  const groups: { name?: string; items: NavItem[] }[] = [];
  for (const it of items) {
    const last = groups[groups.length - 1];
    if (last && last.name === it.group) last.items.push(it);
    else groups.push({ name: it.group, items: [it] });
  }

  return (
    <>
      {/* Desktop: vertikale gruppierte Nav */}
      <nav className="hidden lg:flex flex-col gap-4 w-56 shrink-0 py-6 pl-5 md:pl-8 pr-2 overflow-y-auto border-r border-line">
        {groups.map((g, gi) => (
          <div key={g.name ?? `g${gi}`} className="flex flex-col gap-0.5">
            {g.name && (
              <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-fg-muted/60">{g.name}</div>
            )}
            {g.items.map((t) => {
              const isActive = active === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => onChange(t.id)}
                  className={cn(
                    "group relative flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left",
                    isActive ? "bg-ember/10 text-ember" : "text-fg-muted hover:text-fg hover:bg-white/[0.04]"
                  )}
                >
                  {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-ember rounded-r-full" />}
                  <span className={cn("shrink-0 transition-colors", isActive ? "text-ember" : "text-fg-muted group-hover:text-fg")}>{t.icon}</span>
                  <span className="flex-1 truncate">{t.label}</span>
                  {t.badge !== undefined && t.badge > 0 && (
                    <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-ember text-ink text-[10px] font-semibold flex items-center justify-center">
                      {t.badge > 99 ? "99+" : t.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Mobile: horizontale Scroll-Leiste (flach, mit Gruppen-Trennern) */}
      <div className="lg:hidden px-5 md:px-8 border-b border-line bg-surface/40">
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-none pb-px">
          {groups.map((g, gi) => (
            <div key={g.name ?? `mg${gi}`} className="flex items-center gap-1">
              {gi > 0 && <span className="w-px h-5 bg-line mx-1 shrink-0" />}
              {g.items.map((t) => {
                const isActive = active === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => onChange(t.id)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors",
                      isActive ? "bg-ember/10 text-ember" : "text-fg-muted hover:text-fg hover:bg-white/[0.04]"
                    )}
                  >
                    {t.icon}
                    {t.label}
                    {t.badge !== undefined && t.badge > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-ember text-ink text-[10px] font-semibold flex items-center justify-center">
                        {t.badge > 99 ? "99+" : t.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
